import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { LoadingSpinner, StatusPill } from '../atoms';
import { useErrorHandlerContext, useOpeningElementsControllerContext, useUserLanguage } from '@/customHooks';
import { capitalizeFirstLetter } from '@/helpers';
import { SimpleSupplierProps } from '@/custom-types';
import {
  GetSuppliersSimpleQuery,
  useGetSuppliersSimpleQuery
} from '@/graphql/generated';

type SuppliersSimpleSelectionListProps = {
  selected: (supplier: SimpleSupplierProps) => void
}

const textsData = {
  en: {
    name: 'Name',
    status: 'Status',
    address: 'Location',
  },
  es: {
    name: 'Nombre',
    status: 'Estado',
    address: 'Direccion'
  }
}

const StyledGridTable = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg} ;
  padding-bottom: ${({ theme }) => theme.spacing.lg} ;
  border-radius: 12px;
  display: grid;
  grid-template-columns: 64px 2fr 1fr 2fr;
  row-gap: 10px;
`;

const StyledGridHeader = styled.div`
  display: contents;
  & > div {
    font-weight: bold;
    text-align: left;
    padding: 15px;
    border-top: 1px solid grey;
    border-bottom: 1px solid gray;
  }
`;

const StyledGridRow = styled.div`
  display: contents;

  & > div {
    padding: 15px;
    background-color: ${({ theme }) => theme.colors.background2};
    font-size: 14px;
    padding-top: 1rem;
    padding-bottom: 1rem;
    align-self: stretch;
    place-content: center;
    border-bottom: 1px solid lightgray;
  }
`;

type SupplierData = NonNullable<GetSuppliersSimpleQuery['suppliers']>['items'][number]

const dataFlatter = (suppliers: SupplierData[] | undefined) => {
  if (!suppliers || suppliers.length === 0) return []

  return suppliers.map((supplier) => {
    const city = supplier.address.city?.name?.trim() || ''
    const state = supplier.address.state?.name?.trim() || ''
    
    let address = ''
    if (city && state) {
      address = `${city}, ${state}`
    } else if (city) {
      address = city
    } else if (state) {
      address = state
    }
    
    return {
      firstName: supplier.firstName,
      lastName: supplier.lastName,
      isActive: supplier.isActive,
      id: supplier.user.id,
      address,
    }
  })
}

export const SuppliersSimpleSelectionList = ({ selected }: SuppliersSimpleSelectionListProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { state } = useOpeningElementsControllerContext()
  
  const { open: errorHandle } = useErrorHandlerContext()

  const { data, error, loading, refetch } = useGetSuppliersSimpleQuery()
  
  if (error) errorHandle(error);
  
  const tableData = dataFlatter(data?.suppliers?.items)

  useEffect(() => {
    if (!state.isCreateOwnerFormModalOpen) refetch()
    
  }, [state.isCreateOwnerFormModalOpen, refetch])

  useEffect(() => {
    if (!data?.suppliers?.items) return
    const supplier = data.suppliers.items.find((supplier) => selectedId === supplier.user.id)

    if(!supplier) return
    
    selected(supplier)
  }, [selectedId])

  return (
    <>
      {
        loading
        ? <LoadingSpinner />
        : (
            <StyledGridTable>
              {/* Encabezado */}
              <StyledGridHeader>
                <div></div>
                <div>{texts.name}</div>
                <div>{texts.status}</div>
                <div>{texts.address}</div>
              </StyledGridHeader>

              {/* Filas dinámicas */}
              {tableData.map((data) => (
                <StyledGridRow key={data.id}>
                  <div>
                    <input
                      type="radio"
                      checked={selectedId === data.id}
                      onChange={() => setSelectedId(data.id)}
                      disabled={!data.isActive}
                    />
                  </div>
                  <div>{`${capitalizeFirstLetter(data.firstName)} ${capitalizeFirstLetter(data.lastName)}`}</div>
                  <div><StatusPill isActive={data.isActive} /></div>
                  <div>{data.address}</div>
                </StyledGridRow>
              ))}
            </StyledGridTable>
          )
      }
    </>
  );
};
